import React from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, Circle } from 'lucide-react';

const TodoTaskDashboard = ({ todoTaskAnalysis }) => {
  if (!todoTaskAnalysis || todoTaskAnalysis.totalTasks === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No todo task data available</p>
      </motion.div>
    );
  }

  const { totalTasks, completedTasks, completionRate, taskCategories, procrastinationScore, taskConsistency } = todoTaskAnalysis;

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (completionRate / 100) * circumference;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-pink-600 rounded-lg">
          <CheckSquare className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Todo Tasks</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{completedTasks} of {totalTasks} completed</p>
        </div>
      </div>

      {/* Completion Ring */}
      <div className="flex items-center justify-center mb-6">
        <div className="relative">
          <svg width="140" height="140" className="transform -rotate-90">
            <circle cx="70" cy="70" r={radius} stroke="#e5e7eb" strokeWidth="10" fill="none" />
            <motion.circle
              cx="70"
              cy="70"
              r={radius}
              stroke="#ec4899"
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-3xl font-bold text-pink-600">{completionRate}%</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Complete</p>
            </div>
          </div>
        </div>
      </div>

      {/* Task Categories */}
      {taskCategories && taskCategories.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Task Categories</h4>
          {taskCategories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <Circle className="h-4 w-4 text-pink-600" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{category.category}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">{category.count} tasks</span>
                <span className="text-xs font-bold text-pink-600">{category.completionRate}%</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default TodoTaskDashboard;
